import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EmailPreviewHydrationData } from '@galaxy/email-ui/email-builder';
import { TranslateService } from '@ngx-translate/core';
import { AccountGroup, ProjectionFilter } from '@vendasta/account-group';
import { BusinessSelectorDialogComponent, DialogData } from '@vendasta/businesses';
import { oneResultFrom } from '@vendasta/rx-utils';

export interface SelectedBusiness {
  accountGroupId: string;
  name: string;
  hasSalesperson: boolean;
  hasSnapshot: boolean;
}

export type Layout = 'vertical' | 'horizontal';

@Component({
  selector: 'app-preview-business-selector',
  templateUrl: './preview-business-selector.component.html',
  styleUrls: ['./preview-business-selector.component.scss'],
})
export class PreviewBusinessSelectorComponent {
  name: string;
  showWarning: boolean;
  warningKey?: string;
  warning: string;
  dialogData?: DialogData;

  @Input() set hydrationData(v: EmailPreviewHydrationData) {
    const biz = v?.business;
    this.name = biz?.name || 'None selected';
    // TODO: Bring back translation once weblate/lexicon are working again
    // this.name = biz?.name || this.translate.instant('EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NONE_SELECTED');
    this.showWarning = this.shouldShowWarning(biz);
    this.warningKey = biz ? this.getWarningKey(biz) : '';
    this.warning = biz ? this.getWarning(biz) : '';
    this.dialogData = {
      partnerId: v?.partnerID,
      marketId: v?.marketID,
      projectionFilterOverride: {
        napData: true,
        accountGroupExternalIdentifiers: true,
        snapshotReports: true,
      } as ProjectionFilter,
    };
  }

  @Input() layout: Layout = 'horizontal';

  @Output() businessSelected = new EventEmitter<SelectedBusiness>();

  private shouldShowWarning(biz: { hasSnapshot: boolean; hasSalesperson: boolean }): boolean {
    if (!biz) {
      return false;
    }
    return !(biz?.hasSalesperson && biz?.hasSnapshot);
  }

  /**
   * @deprecated: Use getWarningKey
   */
  private getWarning(biz: {
    accountGroupId: string;
    name: string;
    hasSnapshot: boolean;
    hasSalesperson: boolean;
  }): string {
    if (!biz.hasSalesperson && !biz.hasSnapshot) {
      return 'Business has no snapshot reports and no assigned salesperson. Emails containing related content will show placeholder text.';
    }
    if (!biz.hasSnapshot) {
      return 'Business has no snapshot reports. Emails containing snapshot-related content will show placeholder text.';
    }
    if (!biz.hasSalesperson) {
      return 'Business has no assigned salesperson. Emails containing salesperson-related content will show placeholder text.';
    }
    return '';
  }

  private getWarningKey(biz: {
    accountGroupId: string;
    name: string;
    hasSnapshot: boolean;
    hasSalesperson: boolean;
  }): string {
    if (!biz.hasSalesperson && !biz.hasSnapshot) {
      return 'EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NO_SNAPSHOT_OR_SALESPERSON_WARNING';
    }
    if (!biz.hasSnapshot) {
      return 'EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NO_SNAPSHOT_WARNING';
    }
    if (!biz.hasSalesperson) {
      return 'EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NO_SALESPERSON_WARNING';
    }
    return '';
  }

  constructor(
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
  ) {}

  async openBusinessSelectDialog(): Promise<void> {
    const dialog = this.dialog.open(BusinessSelectorDialogComponent, {
      data: this.dialogData,
    });
    const ag: AccountGroup = await oneResultFrom(dialog.afterClosed());
    if (ag) {
      const sel: SelectedBusiness = {
        accountGroupId: ag.accountGroupId,
        name: ag.napData.companyName,
        hasSnapshot: ag.snapshotReports?.snapshots?.length > 0,
        hasSalesperson: !!ag.accountGroupExternalIdentifiers?.salesPersonId,
      };
      this.name = sel.name;
      this.showWarning = this.shouldShowWarning(sel);
      this.businessSelected.emit(sel);
    }
  }
}
